import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  Policy as PolicyIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Gavel as GavelIcon,
  Security as SecurityIcon,
  Description as DescriptionIcon,
  VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PolicyManagementFeature: React.FC = () => {
  const navigate = useNavigate();

  const benefits = [
    'Centralized repository for all data governance policies',
    'Automated policy enforcement and compliance monitoring',
    'Version control and audit trail for policy changes',
    'Consistent policy application across all DHS components',
    'Reduced compliance risk and regulatory violations',
    'Streamlined policy review and approval workflows'
  ];

  const capabilities = [
    {
      icon: <GavelIcon sx={{ color: 'primary.main' }} />,
      title: 'Policy Authoring',
      description: 'Create and maintain comprehensive data governance policies and standards'
    },
    {
      icon: <SecurityIcon sx={{ color: 'primary.main' }} />,
      title: 'Compliance Monitoring',
      description: 'Automated monitoring and alerts for policy violations and exceptions'
    },
    {
      icon: <DescriptionIcon sx={{ color: 'primary.main' }} />,
      title: 'Documentation',
      description: 'Comprehensive documentation of policies, procedures, and standards'
    },
    {
      icon: <VerifiedUserIcon sx={{ color: 'primary.main' }} />,
      title: 'Approval Workflows',
      description: 'Structured workflows for policy review, approval, and publication'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: 'primary.main' }}
      >
        Back
      </Button>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PolicyIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
          <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Policy Management
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Documentation and enforcement of data governance policies, standards, and procedures
        </Typography>
        
        {/* Access Application Button */}
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/governance/policies')}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'primary.dark'
            }
          }}
        >
          Access Policy Management Application →
        </Button>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderLeft: '4px solid #003366' }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          Overview
        </Typography>
        <Typography paragraph>
          Policy Management provides a centralized platform for creating, maintaining, and enforcing data governance policies across all DHS components. It ensures consistent application of standards and procedures while maintaining compliance with regulatory requirements.
        </Typography>
        <Typography paragraph>
          The framework supports the complete policy lifecycle from authoring through approval, publication, enforcement, and retirement. Automated monitoring and alerts help ensure ongoing compliance and reduce regulatory risk.
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, mb: 4, bgcolor: '#f8f9fa' }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          Key Benefits
        </Typography>
        <List>
          {benefits.map((benefit, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText primary={benefit} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
        Core Capabilities
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {capabilities.map((capability, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #003366' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {capability.icon}
                  <Typography variant="h6" sx={{ ml: 1, color: 'primary.main', fontWeight: 600 }}>
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          Common Use Cases
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Data Classification
          </Typography>
          <Typography paragraph>
            Define and enforce policies for classifying sensitive data across all systems.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Access Control
          </Typography>
          <Typography paragraph>
            Establish and maintain policies for data access, sharing, and security controls.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Retention & Disposal
          </Typography>
          <Typography paragraph>
            Manage policies for data retention periods and secure disposal procedures.
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 4, bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Ready to Strengthen Your Data Governance?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Learn more about how E-Unify's Policy Management can help your organization
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/contact')}
          sx={{
            bgcolor: 'white',
            color: 'primary.main',
            '&:hover': { bgcolor: '#f8f9fa' }
          }}
        >
          Contact Us
        </Button>
      </Paper>
    </Container>
  );
};

export default PolicyManagementFeature;
